import React, { useEffect, useState } from 'react'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CLoadingButton,
  CButton,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilArrowRight,
  cilFilter,
  cilGroup,
  cilOptions,
  cilPeople,
  cilShieldAlt,
  cilStar,
} from '@coreui/icons'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CommonService from 'src/service/CommonService'
import GeneralService from 'src/service/GeneralService'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import Loader from 'src/components/Loader'
import StepperBar from '../SetupProgress/StepperBar'
import TeamBudget from './TeamBudget'
import PlayerSelectionMode from './PlayerSelectionMode'
import PlayerSelectionLimit from './PlayerSelectionLimit'
import PlayerCategoryRestrictions from './PlayerCategoryRestrictions'
import CaptainMultipliers from './CaptainMultipliers'
import GameRulesSection from './GameRulesSection'
import GameStructureSummary from './GameStructureSummary'

const SectionCard = ({ icon, title, children }) => (
  <CCard className="mb-4">
    <CCardHeader className="d-flex align-items-center">
      <CIcon icon={icon} className="me-2" />
      <strong>{title}</strong>
    </CCardHeader>
    <CCardBody>{children}</CCardBody>
  </CCard>
)

const validationSchema = Yup.object().shape({
  team_size: Yup.string().required('Team size is required'),
  salary_cap: Yup.number()
    .typeError('Salary cap must be a number')
    .min(50, 'Salary cap must be at least $50m')
    .max(150, 'Salary cap cannot exceed $150m')
    .required('Salary cap is required'),
  trades: Yup.number()
    .typeError('Trades must be a number')
    .min(0, 'Trades cannot be negative')
    .max(100, 'Trades cannot exceed 100')
    .required('Trades is required'),
  max_players_per_team: Yup.string().required('Max players per team is required'),
  pcr_term: Yup.string().when('pcr_enabled', {
    is: true,
    then: (schema) => schema.required('PCR term is required'),
  }),
  pcr_max_players: Yup.string().when('pcr_enabled', {
    is: true,
    then: (schema) => schema.required('Max PCR players is required'),
  }),
  game_visibility: Yup.string().required('Game visibility is required'),
  squad_alert: Yup.string().required('Squad alert is required'),
  min_bat: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Min should be 1')
        .max(5, 'Min should not be more than 5')
        .required('Min is required'),
  }),
  max_bat: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Max should be at least 1')
        .max(5, 'Max should be 5')
        .required('Max is required'),
  }),
  min_bowl: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Min should be 1')
        .max(5, 'Min should not be more than 5')
        .required('Min is required'),
  }),
  max_bowl: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Max should be at least 1')
        .max(5, 'Max should be 5')
        .required('Max is required'),
  }),
  min_ar: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Min should be 1')
        .max(5, 'Min should not be more than 5')
        .required('Min is required'),
  }),
  max_ar: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Max should be at least 1')
        .max(5, 'Max should be 5')
        .required('Max is required'),
  }),
  min_wk: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Min should be 1')
        .max(5, 'Min should not be more than 5')
        .required('Min is required'),
  }),
  max_wk: Yup.number().when('player_selection_mode', {
    is: 'based',
    then: (schema) =>
      schema
        .min(1, 'Max should be at least 1')
        .max(5, 'Max should be 5')
        .required('Max is required'),
  }),
})

const toFormValues = (gameData) => ({
  team_size: gameData?.team_size || '',
  salary_cap: gameData?.salary_cap || '',
  trades: gameData?.trades || '',
  player_selection_mode: gameData?.player_selection_mode || 'based',
  min_bat: gameData?.multiplayer_data?.min_bats || 1,
  max_bat: gameData?.multiplayer_data?.max_bats || 5,
  min_bowl: gameData?.multiplayer_data?.min_bowls || 1,
  max_bowl: gameData?.multiplayer_data?.max_bowls || 5,
  min_ar: gameData?.multiplayer_data?.min_ar || 1,
  max_ar: gameData?.multiplayer_data?.max_ar || 5,
  min_wk: gameData?.multiplayer_data?.min_wks || 1,
  max_wk: gameData?.multiplayer_data?.max_wks || 5,
  max_players_per_team:
    Number(gameData?.team_selection_limit_status) === 1
      ? String(gameData?.team_selection_limit || 1)
      : '0',
  pcr_enabled: Number(gameData?.external_player_limit_status) === 1,
  pcr_term: gameData?.external_player_tag || '',
  pcr_max_players: gameData?.external_player_limit || 1,
  game_visibility: gameData?.game_privacy || '1',
  squad_alert: gameData?.player_alert ?? '0',
})

const changed = (a, b) => String(a ?? '') !== String(b ?? '')

const GameStructure = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [gameData, setGameData] = useState({})
  const [externalPlayerList, setExternalPlayerList] = useState([])
  const [isTeamExists, setIsTeamExists] = useState(false)
  const [gameType, setGameType] = useState()

  useEffect(() => {
    CommonService.getGameType().then((res) => {
      if (res.status === 200) {
        setGameType(res.data)
      }
    })
  }, [])

  const fetchGameStructure = () =>
    Promise.all([CommonService.gameStructureInfo(), GeneralService.getUserGameInfo()]).then(
      ([structureRes, gameInfoRes]) => {
        if (structureRes.status === 200) {
          setGameData({
            ...structureRes.game_data,
            player_selection_mode: gameInfoRes?.data?.team_view === 1 ? 'free' : 'based',
            multiplayer_data: structureRes.multiplayer_data,
          })
          setExternalPlayerList(structureRes.externalPlayerList || [])
          setIsTeamExists(!!structureRes.is_team_exists)
        }
      },
    )

  useEffect(() => {
    fetchGameStructure().finally(() => setIsLoading(false))
  }, [])

  const saveChanges = async (values) => {
    setSaving(true)
    try {
      const baseline = toFormValues(gameData)

      if (!isTeamExists && changed(values.team_size, baseline.team_size)) {
        await CommonService.editGameStructure({ type: 1, structure: values.team_size })
      }

      const modeChanged = values.player_selection_mode !== baseline.player_selection_mode
      if (modeChanged) {
        await GeneralService.changeUserGameAddOnStatus({
          addOnType: 'team_view',
          status: values.player_selection_mode === 'free',
        })
      }

      const positionFields = [
        'min_bat',
        'max_bat',
        'min_bowl',
        'max_bowl',
        'min_ar',
        'max_ar',
        'min_wk',
        'max_wk',
      ]
      const positionChanged = positionFields.some((key) => changed(values[key], baseline[key]))
      if (
        values.player_selection_mode === 'based' &&
        !isTeamExists &&
        (modeChanged || positionChanged)
      ) {
        await PlayerService.savePlayerStructure({
          min_bat: values.min_bat,
          max_bat: values.max_bat,
          min_bowl: values.min_bowl,
          max_bowl: values.max_bowl,
          min_ar: values.min_ar,
          max_ar: values.max_ar,
          min_wk: values.min_wk,
          max_wk: values.max_wk,
        })
      }

      if (changed(values.game_visibility, baseline.game_visibility)) {
        await CommonService.updateGamePrivacy({ privacy: values.game_visibility })
      }

      const tasks = []
      if (changed(values.salary_cap, baseline.salary_cap)) {
        tasks.push(CommonService.editGameStructure({ type: 2, salary: values.salary_cap }))
      }
      if (changed(values.trades, baseline.trades)) {
        tasks.push(CommonService.editPowerControl({ type: 'trades_status', trades: values.trades }))
      }
      if (changed(values.squad_alert, baseline.squad_alert)) {
        tasks.push(CommonService.updatePlayerAlert({ player_alert: values.squad_alert }))
      }
      if (changed(values.max_players_per_team, baseline.max_players_per_team)) {
        tasks.push(
          CommonService.updateTeamSelectionLimit({
            team_selection_limit_status: Number(values.max_players_per_team) === 0 ? 0 : 1,
            team_selection_limit: values.max_players_per_team,
          }),
        )
      }
      if (
        values.pcr_enabled !== baseline.pcr_enabled ||
        changed(values.pcr_term, baseline.pcr_term) ||
        changed(values.pcr_max_players, baseline.pcr_max_players)
      ) {
        tasks.push(
          CommonService.updateOverseasLimit({
            external_player_limit_status: values.pcr_enabled ? 1 : 0,
            external_player_limit: values.pcr_max_players,
            external_player_tag: values.pcr_term,
          }),
        )
      }

      await Promise.all(tasks)
      await fetchGameStructure()

      ToastComponent('Game Structure updated successfully.', 'success')
      return true
    } catch (e) {
      ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
      return false
    } finally {
      setSaving(false)
    }
  }

  const formik = useFormik({
    initialValues: toFormValues(gameData),
    enableReinitialize: true,
    validationSchema,
    onSubmit: saveChanges,
  })

  // Position limits are meaningless while in Position Free mode, so the backend stores a sentinel
  // (100) for them there. Switching to Position Based can surface that stale sentinel here, which
  // fails the max-5-per-position rule. Snap any out-of-range value back to a sane default (1-5) so
  // the admin lands on a valid, editable state instead of a save that's blocked by old data.
  useEffect(() => {
    if (formik.values.player_selection_mode !== 'based') return
    const defaults = {
      min_bat: 1,
      max_bat: 5,
      min_bowl: 1,
      max_bowl: 5,
      min_ar: 1,
      max_ar: 5,
      min_wk: 1,
      max_wk: 5,
    }
    Object.entries(defaults).forEach(([key, fallback]) => {
      const value = Number(formik.values[key])
      const isMin = key.startsWith('min_')
      const invalid = isMin ? !(value >= 1) : !(value >= 1 && value <= 5)
      if (invalid) {
        formik.setFieldValue(key, fallback)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.player_selection_mode, gameData])

  const handleFieldChange = (field, value) => formik.setFieldValue(field, value)

  const handleNext = async () => {
    const errors = await formik.validateForm()
    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    )
    if (Object.keys(errors).length > 0) {
      ToastComponent('Please fix the errors before continuing.', 'error')
      return
    }
    const ok = await saveChanges(formik.values)
    if (ok) {
      navigate('/rounds')
    }
  }

  const pcrTermLabel =
    externalPlayerList?.find((item) => item.id === Number(formik.values.pcr_term))?.name || ''

  const currencySymbol = gameData?.default_salary_symbol ?? '$'

  if (isLoading) {
    return (
      <>
        <StepperBar activeStepKey="game_structure" />
        <Loader />
      </>
    )
  }

  return (
    <>
      <StepperBar activeStepKey="game_structure" />
      <CForm onSubmit={formik.handleSubmit}>
        <CRow>
          <CCol xs={12} lg={8}>
            <SectionCard icon={cilGroup} title="Team & Budget">
              <TeamBudget
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                onChange={handleFieldChange}
                disabled={isTeamExists}
                currencySymbol={currencySymbol}
              />
            </SectionCard>

            <SectionCard icon={cilOptions} title="Player Selection Mode">
              <PlayerSelectionMode
                values={formik.values}
                errors={formik.errors}
                touched={formik.touched}
                onChange={handleFieldChange}
                isTeamExists={isTeamExists}
              />
            </SectionCard>

            {gameType !== 1 && (
              <SectionCard icon={cilPeople} title="Player Selection Limit">
                <PlayerSelectionLimit
                  values={formik.values}
                  onChange={handleFieldChange}
                  teamSize={formik.values.team_size}
                  isTeamExists={isTeamExists}
                />
              </SectionCard>
            )}

            <SectionCard icon={cilFilter} title="Player Category Restrictions (PCR)">
              <PlayerCategoryRestrictions
                values={formik.values}
                onChange={handleFieldChange}
                teamSize={formik.values.team_size}
                externalPlayerList={externalPlayerList}
                isTeamExists={isTeamExists}
              />
            </SectionCard>

            <SectionCard icon={cilStar} title="Captain & Multipliers">
              <CaptainMultipliers />
            </SectionCard>

            <SectionCard icon={cilShieldAlt} title="Alerts & Rules">
              <GameRulesSection
                values={formik.values}
                onChange={handleFieldChange}
                gameData={gameData}
              />
            </SectionCard>
          </CCol>

          <CCol xs={12} lg={4}>
            <GameStructureSummary
              values={formik.values}
              pcrTermLabel={pcrTermLabel}
              currencySymbol={currencySymbol}
            />
          </CCol>
        </CRow>

        <CCard className="mb-4 mt-2">
          <CCardBody className="d-flex justify-content-between">
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => navigate('/setup-progress')}
              type="button"
            >
              <CIcon icon={cilArrowLeft} className="me-2" />
              Back: About Game
            </CButton>
            <div className="d-flex gap-2">
              <CLoadingButton type="submit" color="secondary" variant="outline" loading={saving}>
                Save Changes
              </CLoadingButton>
              <CLoadingButton type="button" color="primary" loading={saving} onClick={handleNext}>
                Next: Rounds
                <CIcon icon={cilArrowRight} className="ms-2" />
              </CLoadingButton>
            </div>
          </CCardBody>
        </CCard>
      </CForm>
    </>
  )
}

export default GameStructure
